/**
 * src/utils/navigationGuard.ts
 * ページ離脱時の未保存確認ガード（アプリ内遷移用）
 *
 * beforeunload はブラウザのリロード・タブ閉じにしか効かないため、
 * React Router の <Link>/<NavLink> や Sidebar/TopBar/MobileDrawer など
 * 共通ナビゲーションからのアプリ内遷移は、この仕組みで別途ガードする。
 *
 * ページ側は useNavigationGuard(isDirty) を呼ぶだけでよい。
 * 呼び出したページがマウントされている間だけガードが有効になり、
 * アンマウント時に自動で解除されるため、他のページには一切影響しない。
 * （BrowserRouter 構成のため useBlocker は使えない前提での実装）
 */
import { useEffect, useRef } from 'react'

export const LEAVE_CONFIRM_MESSAGE =
  '入力途中の内容があります。このまま離れると保存されません。移動してもよろしいですか？'

let activeGuard: (() => boolean) | null = null

/**
 * ナビゲーション実行前に呼び出す。ガード登録中のページで dirty な場合のみ
 * confirm ダイアログを出し、その結果（true=遷移してよい）を返す。
 * ガード未登録、または dirty でなければ常に true。
 */
export function confirmLeave(): boolean {
  if (activeGuard && activeGuard()) {
    return window.confirm(LEAVE_CONFIRM_MESSAGE)
  }
  return true
}

/** ページ側で呼ぶフック。isDirty の間だけ confirmLeave() が確認を出すようにする。 */
export function useNavigationGuard(isDirty: boolean) {
  const dirtyRef = useRef(isDirty)
  useEffect(() => { dirtyRef.current = isDirty }, [isDirty])

  useEffect(() => {
    activeGuard = () => dirtyRef.current
    return () => { activeGuard = null }
  }, [])
}
